import { useEffect, useState, useRef, useMemo } from 'react'
import isEqual from 'lodash/isEqual'

export const ROLL_OUT_DURATION_MS = 450
export const ROLL_IN_DELAY_MS = 200
export const ROLL_IN_DURATION_MS = 350
export const ROLLING_ANIMATION_DURATION_MS = ROLL_IN_DELAY_MS + ROLL_IN_DURATION_MS

type UseRollingAnimationProps<T> = {
  value: T
  isEqual?: (a: T, b: T) => boolean
}

type AnimationState = 'idle' | 'animating'

/**
 * Generic hook for rolling animation that tracks value changes and provides
 * current, previous values, and animation state.
 * 
 * Uses serialized value in dependency array to detect deep changes efficiently.
 * 
 * @param value - The current value to track
 * @param isEqual - Optional custom equality function. Defaults to lodash isEqual for deep comparison.
 * @returns Object with currentValue, previousValue, and isAnimating state
 */
export const useRollingAnimation = <T,>({
  value,
  isEqual: customIsEqual
}: UseRollingAnimationProps<T>) => {
  const [currentValue, setCurrentValue] = useState<T>(value)
  const [previousValue, setPreviousValue] = useState<T>(value)
  const [animationState, setAnimationState] = useState<AnimationState>('idle')
  // Only ref needed: track last seen value for comparison (avoids state dependency)
  const lastSeenValueRef = useRef<T>(value)
  const isInitialMount = useRef(true)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Serialize value for dependency array - detects deep changes
  // Using JSON.stringify is fast for dependency tracking, lodash isEqual for actual comparison
  // Memoized to avoid recalculating on every render
  const valueSerialized = useMemo(() => {
    try {
      return JSON.stringify(value)
    } catch {
      // Fallback to string representation if JSON.stringify fails
      return String(value)
    }
  }, [value])

  // Equality function - memoized to avoid recreation
  // Fast path: reference equality check before expensive deep comparison
  const equalityFn = useMemo(() => {
    if (customIsEqual) {
      return customIsEqual
    }
    return (a: T, b: T): boolean => {
      // Fast path: reference equality (most common case)
      if (a === b) return true
      // Deep comparison only when references differ
      return isEqual(a, b)
    }
  }, [customIsEqual])

  // Set CSS custom properties for animation durations (only once on mount)
  useEffect(() => {
    document.documentElement.style.setProperty('--rolling-out-duration', `${ROLL_OUT_DURATION_MS / 1000}s`)
    document.documentElement.style.setProperty('--rolling-in-delay', `${ROLL_IN_DELAY_MS / 1000}s`)
    document.documentElement.style.setProperty('--rolling-in-duration', `${ROLL_IN_DURATION_MS / 1000}s`)
  }, [])

  // Main effect - triggers on any deep change to value
  useEffect(() => {
    // Skip animation on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false
      lastSeenValueRef.current = value
      setCurrentValue(value)
      return
    }

    // Check if value has actually changed using deep equality
    const hasChanged = !equalityFn(value, lastSeenValueRef.current)

    if (hasChanged) {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      // Store current displayed value as previous and start animation
      // Read currentValue from state (it's the value before this update)
      setPreviousValue(currentValue)
      setAnimationState('animating')

      // Update current value immediately (new content will enter from bottom)
      setCurrentValue(value)
      lastSeenValueRef.current = value

      // After animation completes, return to idle
      const totalAnimationTime = ROLL_IN_DELAY_MS + ROLL_IN_DURATION_MS
      timeoutRef.current = setTimeout(() => {
        setAnimationState('idle')
        setPreviousValue(value)
        timeoutRef.current = null
      }, totalAnimationTime)
    }

    // Cleanup on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [valueSerialized, value, equalityFn])

  return {
    currentValue,
    previousValue,
    isAnimating: animationState === 'animating'
  }
}
